/**
 * Add-a-price-override form shared by the device and feature pricing
 * config grids — lets a user scope a new override to a site group or,
 * optionally, a single site within that group.
 */
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SiteAutocomplete from '@sophocles/components/SiteAutocomplete';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';

const initialFormState = {
  siteGroupId: null,
  siteGroupData: null,
  overrideForSite: false,
  siteId: null,
  siteData: null,
  price: '',
};

function AddPricingOverrideDialog({ open, onClose, onAdd, customerId, entityName, existingOverrides = [] }) {
  const { t } = useTranslation();
  const [formState, setFormState] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  // Compute IDs to exclude from autocompletes based on existing overrides
  const ignoredSiteGroupIds = useMemo(
    () =>
      existingOverrides
        .filter(o => o.site_group && !o.site) // Only site group overrides (not sites within a group)
        .map(o => o.site_group.id),
    [existingOverrides],
  );

  const ignoredSiteIds = useMemo(() => existingOverrides.filter(o => o.site).map(o => o.site.id), [existingOverrides]);

  const handleClose = useCallback(() => {
    setFormState(initialFormState);
    setErrors({});
    onClose();
  }, [onClose]);

  const handleSiteGroupChange = useCallback((e, value, option) => {
    setFormState(prev => ({
      ...prev,
      siteGroupId: value,
      siteGroupData: option,
      // Reset site when site group changes
      siteId: null,
      siteData: null,
      overrideForSite: false,
    }));
    setErrors(prev => ({ ...prev, siteGroup: null }));
  }, []);

  const handleOverrideForSiteChange = useCallback(e => {
    setFormState(prev => ({
      ...prev,
      overrideForSite: e.target.checked,
      // Reset site selection when toggling off
      ...(e.target.checked ? {} : { siteId: null, siteData: null }),
    }));
    setErrors(prev => ({ ...prev, site: null }));
  }, []);

  const handleSiteChange = useCallback((e, option) => {
    const siteId = e.target.value;
    setFormState(prev => ({
      ...prev,
      siteId,
      siteData: option,
    }));
    setErrors(prev => ({ ...prev, site: null }));
  }, []);

  const handlePriceChange = useCallback(e => {
    setFormState(prev => ({ ...prev, price: e.target.value }));
    setErrors(prev => ({ ...prev, price: null }));
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};

    if (!formState.siteGroupId) {
      newErrors.siteGroup = t('billing.pricingConfig.overrideSiteGroupRequired');
    }

    if (formState.overrideForSite && !formState.siteId) {
      newErrors.site = t('billing.pricingConfig.overrideSiteRequired');
    }

    if (!formState.price || parseFloat(formState.price) < 0) {
      newErrors.price = t('billing.pricingConfig.overridePriceHelper');
    }

    // Check for duplicate overrides
    if (!formState.overrideForSite && formState.siteGroupId) {
      // Site group override
      const isDuplicate = existingOverrides.some(o => o.site_group?.id === formState.siteGroupId && !o.site);
      if (isDuplicate) {
        newErrors.siteGroup = t('billing.pricingConfig.overrideSiteGroupDuplicate');
      }
    }

    if (formState.overrideForSite && formState.siteId) {
      // Site override
      const isDuplicate = existingOverrides.some(o => o.site?.id === formState.siteId);
      if (isDuplicate) {
        newErrors.site = t('billing.pricingConfig.overrideSiteDuplicate');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState, existingOverrides, t]);

  const handleAdd = useCallback(() => {
    if (!validate()) {
      return;
    }

    const override = {
      site_group: !formState.overrideForSite
        ? { id: formState.siteGroupId, name: formState.siteGroupData?.label }
        : null,
      site: formState.overrideForSite
        ? {
            id: formState.siteId,
            name: formState.siteData?.label,
            site_group: { id: formState.siteGroupId, name: formState.siteGroupData?.label },
          }
        : null,
      price: formState.price,
    };

    onAdd(override);
    handleClose();
  }, [formState, validate, onAdd, handleClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('billing.pricingConfig.addOverrideForTitle', { name: entityName })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <SiteGroupAutocomplete
            name="siteGroup"
            customerId={customerId}
            value={formState.siteGroupId}
            onChange={handleSiteGroupChange}
            label={t('billing.pricingConfig.overrideSiteGroup')}
            error={!!errors.siteGroup}
            helperText={errors.siteGroup}
            fullWidth
            ignoreIds={!formState.overrideForSite ? ignoredSiteGroupIds : []}
          />

          {formState.siteGroupId && (
            <FormControlLabel
              control={
                <Checkbox checked={formState.overrideForSite} onChange={handleOverrideForSiteChange} size="small" />
              }
              label={t('billing.pricingConfig.overrideSite')}
              sx={{ mt: -1 }}
            />
          )}

          {formState.overrideForSite && (
            <>
              <SiteAutocomplete
                name="site"
                siteGroupId={formState.siteGroupId}
                value={formState.siteId}
                onChange={handleSiteChange}
                error={!!errors.site}
                helperText={errors.site}
                disabled={!formState.siteGroupId}
                fullWidth
                ignoreIds={ignoredSiteIds}
              />
              <FormHelperText sx={{ mt: -1 }}>{t('billing.pricingConfig.overrideSiteHelper')}</FormHelperText>
            </>
          )}

          <TextField
            name="price"
            label={t('billing.pricingConfig.overridePrice')}
            type="number"
            value={formState.price}
            onChange={handlePriceChange}
            error={!!errors.price}
            helperText={errors.price}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ step: '0.01', min: '0' }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('billing.pricingConfig.cancel')}</Button>
        <Button onClick={handleAdd} variant="contained" color="primary">
          {t('billing.pricingConfig.addOverride')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddPricingOverrideDialog;
